/**
 * HTTP client for Lanonasis API
 * Supports Edge Function envelope format with caching for offline fallback
 */

import { ConfigurationError } from "../core/errors.js";
import { EdgeFunctionResponse, ProcessingMode } from "../core/types.js";
import { adaptResponse, ResponseCache, UsageInfo, TierInfo } from "./response-adapter.js";

export interface HttpClientConfig {
  apiUrl: string;
  apiKey?: string;
  authToken?: string;
  authType?: "apiKey" | "bearer";
  allowMissingAuth?: boolean;
  timeout?: number;
  headers?: Record<string, string>;
  processingMode?: ProcessingMode;
  enableCache?: boolean;
  cacheTTL?: number;
}

export interface ApiResponse<T = any> {
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
  status: number;
}

export interface EnhancedApiResponse<T = any> extends ApiResponse<T> {
  usage?: UsageInfo;
  tier_info?: TierInfo;
  fromCache?: boolean;
}

export class HttpClient {
  private apiUrl: string;
  private apiKey: string | undefined;
  private timeout: number;
  private headers: Record<string, string>;
  private processingMode: ProcessingMode;
  private cache: ResponseCache | null;

  constructor(config: HttpClientConfig) {
    this.apiUrl = config.apiUrl.replace(/\/$/, ""); // Remove trailing slash

    // Determine auth type: explicit config, or infer from provided credentials
    let authType: "apiKey" | "bearer";
    if (config.authType) {
      authType = config.authType;
    } else if (config.apiKey) {
      authType = "apiKey";
    } else if (config.authToken) {
      authType = "bearer";
    } else if (config.allowMissingAuth) {
      // Default to apiKey when no auth provided but missing auth is allowed
      authType = "apiKey";
    } else {
      throw new ConfigurationError(
        "Authentication required: provide apiKey, authToken, or set allowMissingAuth to true"
      );
    }

    const allowMissingAuth = Boolean(config.allowMissingAuth);

    if (authType === "apiKey") {
      if (!config.apiKey && !allowMissingAuth) {
        throw new ConfigurationError("API key is required when authType is 'apiKey'");
      }
      if (config.apiKey && !config.apiKey.startsWith("lano_")) {
        throw new ConfigurationError(
          "Invalid API key format. API key should start with 'lano_'"
        );
      }
    } else if (!config.authToken && !allowMissingAuth) {
      throw new ConfigurationError("Bearer token is required when authType is 'bearer'");
    }

    this.apiKey = config.apiKey;
    this.timeout = config.timeout || 30000;
    this.processingMode = config.processingMode || "api";

    const baseHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      ...config.headers,
    };

    if (authType === "apiKey" && this.apiKey && !("X-API-Key" in baseHeaders)) {
      baseHeaders["X-API-Key"] = this.apiKey;
    }

    if (authType === "bearer" && config.authToken && !("Authorization" in baseHeaders)) {
      baseHeaders["Authorization"] = `Bearer ${config.authToken}`;
    }

    this.headers = baseHeaders;

    // Initialize cache for offline-fallback mode
    if (config.enableCache !== false && this.processingMode === "offline-fallback") {
      this.cache = new ResponseCache(config.cacheTTL || 300000);
    } else {
      this.cache = null;
    }
  }

  /**
   * Get the current processing mode
   */
  getProcessingMode(): ProcessingMode {
    return this.processingMode;
  }

  /**
   * Check if cache is enabled
   */
  isCacheEnabled(): boolean {
    return this.cache !== null;
  }

  /**
   * Clear the response cache
   */
  clearCache(): void {
    this.cache?.clear();
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    const url = `${this.apiUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const options: RequestInit = {
        method,
        headers: this.headers,
        signal: controller.signal,
      };

      if (data && (method === "POST" || method === "PUT" || method === "PATCH")) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);
      clearTimeout(timeoutId);

      const responseData = await response.json() as any;

      if (!response.ok) {
        const errorMessage =
          responseData?.message ||
          responseData?.error?.message ||
          (typeof responseData?.error === "string" ? responseData.error : undefined) ||
          "Request failed";
        return {
          status: response.status,
          error: {
            message: errorMessage,
            code: responseData?.code || responseData?.error?.code,
          },
        };
      }

      return {
        status: response.status,
        data: responseData as T,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          return {
            status: 408,
            error: {
              message: `Request timeout after ${this.timeout}ms`,
              code: "TIMEOUT",
            },
          };
        }

        return {
          status: 0,
          error: {
            message: error.message,
            code: "NETWORK_ERROR",
          },
        };
      }

      return {
        status: 0,
        error: {
          message: "Unknown error occurred",
          code: "UNKNOWN_ERROR",
        },
      };
    }
  }

  /**
   * Enhanced request that handles Edge Function envelope format
   * and supports offline-fallback caching
   */
  private async enhancedRequest<T>(
    method: string,
    endpoint: string,
    data?: any
  ): Promise<EnhancedApiResponse<T>> {
    const cacheKey = data ? JSON.stringify(data) : undefined;

    // Try API request first
    const rawResponse = await this.request<EdgeFunctionResponse<T> | T>(method, endpoint, data);

    // Handle network errors with offline fallback
    if (rawResponse.error?.code === "NETWORK_ERROR" || rawResponse.error?.code === "TIMEOUT") {
      if (this.processingMode === "offline-fallback" && this.cache) {
        const cached = this.cache.get<T>(endpoint, cacheKey ? JSON.parse(cacheKey) : undefined);
        if (cached) {
          return {
            status: 200,
            data: cached,
            fromCache: true,
          };
        }
      }
      // No cache available, return original error
      return {
        status: rawResponse.status,
        error: rawResponse.error,
        fromCache: false,
      };
    }

    // Adapt response (handles both direct and envelope formats)
    const adapted = adaptResponse<T>(rawResponse);

    // Cache successful responses for offline fallback
    if (!adapted.error && adapted.data && this.cache) {
      this.cache.set(endpoint, adapted.data, cacheKey ? JSON.parse(cacheKey) : undefined);
    }

    return {
      status: adapted.status,
      data: adapted.data,
      error: adapted.error,
      usage: adapted.usage,
      tier_info: adapted.tier_info,
      fromCache: false,
    };
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>("GET", endpoint);
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>("POST", endpoint, data);
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>("PUT", endpoint, data);
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>("PATCH", endpoint, data);
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>("DELETE", endpoint);
  }

  // Enhanced methods with Edge Function envelope support

  async getEnhanced<T>(endpoint: string): Promise<EnhancedApiResponse<T>> {
    return this.enhancedRequest<T>("GET", endpoint);
  }

  async postEnhanced<T>(endpoint: string, data?: any): Promise<EnhancedApiResponse<T>> {
    return this.enhancedRequest<T>("POST", endpoint, data);
  }

  async putEnhanced<T>(endpoint: string, data?: any): Promise<EnhancedApiResponse<T>> {
    return this.enhancedRequest<T>("PUT", endpoint, data);
  }

  async patchEnhanced<T>(endpoint: string, data?: any): Promise<EnhancedApiResponse<T>> {
    return this.enhancedRequest<T>("PATCH", endpoint, data);
  }

  async deleteEnhanced<T>(endpoint: string): Promise<EnhancedApiResponse<T>> {
    return this.enhancedRequest<T>("DELETE", endpoint);
  }
}
